import cn from 'classnames';
import { FC, ReactNode } from 'react';

import { Icon, type IconNames } from '@/shared/ui';

import styles from './NoteRowTools.module.scss';
import { SettingsPopover } from './SettingsPopover';

type ToolProps = {
  icon: IconNames;
  label: string;
  // The tooltip, when it says more than the label.
  hint?: string;
  danger?: boolean;
  // A round tool in a head or a toolbar rather than a narrow one beside a row.
  head?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  className?: string;
  onClick: () => void;
};

export const RowTool: FC<ToolProps> = ({
  icon,
  label,
  hint,
  danger,
  head,
  disabled,
  hidden,
  className,
  onClick,
}) => (
  <button
    type="button"
    className={cn(
      styles.tool,
      danger && styles.danger,
      head && styles.head,
      className,
    )}
    aria-label={label}
    title={hint ?? label}
    disabled={disabled}
    hidden={hidden}
    onClick={onClick}
  >
    <Icon name={icon} size={16} />
  </button>
);

type RowAction = { label: string; onClick: () => void };

// Settings of the row that open beside it, e.g. when a line is shown.
type RowSettings = {
  icon: IconNames;
  label: string;
  // The row holds such a setting: the tool stays in sight.
  marked: boolean;
  dataComponent?: string;
  content: ReactNode;
};

type RowToolsProps = {
  remove?: RowAction;
  reset?: RowAction;
  settings?: RowSettings;
  // Four narrower tools fit the indent that three take.
  dense?: boolean;
  // The host row places the tools and shows them on hover.
  className?: string;
  // Tools the host renders on its own, after the reset.
  children?: ReactNode;
};

export const RowTools: FC<RowToolsProps> = ({
  remove,
  reset,
  settings,
  dense,
  className,
  children,
}) => (
  <div
    className={cn(styles.tools, dense && styles.dense, className)}
    data-component="workspace-row-tools"
  >
    {remove && (
      <RowTool
        icon="close"
        danger
        label={remove.label}
        onClick={remove.onClick}
      />
    )}
    {reset && (
      <RowTool icon="rotate" label={reset.label} onClick={reset.onClick} />
    )}
    {children}
    {settings && (
      <SettingsPopover
        label={settings.label}
        icon={settings.icon}
        iconSize={16}
        className={cn(
          styles.tool,
          styles.settings,
          settings.marked && styles.pinned,
        )}
        dataComponent={settings.dataComponent}
      >
        {settings.content}
      </SettingsPopover>
    )}
  </div>
);
